import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import swal from 'sweetalert';
import { getAnimals, getAnimalsE } from "../../api/animalsApi";
import { getReproductions, getReproductionsByUser } from "../../api/reproductionApi";
import { getUsers } from "../../api/usersApi";
import { updateFerme } from "../../api/fermeApi";
import { AuthContext } from "../../Contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import './Accueil.css';
import { FaHeartbeat, FaUsers, FaHeart, FaBed, FaPlus, FaCheckCircle, FaBell, FaChartLine, FaTachometerAlt, FaBaby } from 'react-icons/fa';
import { MdPregnantWoman } from 'react-icons/md';
import { GiCow, GiBabyBottle } from 'react-icons/gi';
import { FaEdit } from "react-icons/fa";
import { FiHome, FiMapPin, FiCheckCircle as FiCheckCircleOutline, FiLoader } from "react-icons/fi";
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AdminAccueil = () => {
  const { user, setUser } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [animals, setAnimals] = useState([]);
  const [animalsE, setAnimalsE] = useState([]);
  const [reproductions, setReproductions] = useState([]);
  const [reproductionsE, setReproductionsE] = useState([]);
  const [users, setUsers] = useState([]);
  const [hasAlerted, setHasAlerted] = useState(false);

  const [isFermeModalOpen, setIsFermeModalOpen] = useState(false);
  const [fermeNom, setFermeNom] = useState('');
  const [fermeAdresse, setFermeAdresse] = useState('');
  const [fermeLoading, setFermeLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('welcome') === 'true') {
      swal({
        title: t('adminAccueil.swal.welcomeTitle', { defaultValue: 'Welcome!' }),
        text: t('adminAccueil.swal.welcomeText', {
          name: user?.nom || '',
          farm: user?.ferme?.nom || '',
          defaultValue: 'Welcome {{name}}! Your account and farm "{{farm}}" were created successfully.',
        }),
        icon: 'success',
        button: t('adminAccueil.swal.buttonStart', { defaultValue: 'Start!' }),
      });
    }
  }, [searchParams, t, user?.ferme?.nom, user?.nom]);

  useEffect(() => {
    if (user.role === 'admin') {
      getAnimals().then(data => setAnimals(data));
      getReproductions().then(data => setReproductions(data));
      getUsers().then(data => setUsers(data));
    } else {
      getAnimalsE().then(data => setAnimalsE(data));
      getReproductionsByUser(user.id).then(data => setReproductionsE(data));
    }
  }, [user.id, user.role]);

  useEffect(() => {
    if (user?.ferme) {
      setFermeNom(user.ferme.nom || '');
      setFermeAdresse(user.ferme.adresse || '');
    }
  }, [user?.ferme]);

  const stats = useMemo(() => {
    const repros = user.role === 'admin' ? reproductions : reproductionsE;

    const calculated = {
      total: repros.length,
      gestation: 0,
      velage: 0,
      saillie: 0,
      repos: 0,
      croissance: 0,
      upcomingVelage: 0,
    };

    const today = new Date();

    repros.forEach(r => {
      const statusTitle = (r.status?.titre || '').toLowerCase();
      if (statusTitle.includes('gestation')) calculated.gestation++;
      else if (statusTitle.includes('vel')) calculated.velage++;
      else if (statusTitle.includes('saillie') || statusTitle.includes('oestrus')) calculated.saillie++;
      else if (statusTitle.includes('repos')) calculated.repos++;
      else if (statusTitle.includes('croissance')) calculated.croissance++;

      if (r.date_prevu_velage) {
        const dueDate = new Date(r.date_prevu_velage);
        const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        if (daysUntil >= 0 && daysUntil <= 7) {
          calculated.upcomingVelage++;
        }
      }
    });

    return calculated;
  }, [reproductions, reproductionsE, user.role]);

  useEffect(() => {
    if (user.role !== 'admin' || hasAlerted || reproductions.length === 0) return;

    const alerts = [];
    const today = new Date();

    reproductions.forEach((r) => {
      if (!r.date_prevu_velage || !r.animal?.nom) return;

      const dueDate = new Date(r.date_prevu_velage);
      const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      if (daysUntil === 0) {
        alerts.push(t('adminAccueil.alerts.today', {
          animal: r.animal.nom,
          defaultValue: '{{animal}} must calve TODAY!',
        }));
      } else if (daysUntil > 0 && daysUntil <= 3) {
        alerts.push(t('adminAccueil.alerts.inDays', {
          animal: r.animal.nom,
          days: daysUntil,
          defaultValue: '{{animal}} will calve in {{days}} day(s).',
        }));
      }
    });

    if (alerts.length > 0) {
      swal({
        title: t('adminAccueil.alerts.calvingTitle', { defaultValue: 'Calving Alerts' }),
        text: alerts.join('\n\n'),
        icon: 'warning',
        button: t('adminAccueil.alerts.understood', { defaultValue: 'Understood!' }),
      });
      setHasAlerted(true);
    }
  }, [reproductions, hasAlerted, t, user.role]);

  const quickActions = useMemo(() =>
    user.role === 'admin' ? [
      { title: t('adminAccueil.quickActions.addAnimal', { defaultValue: 'Add Animal' }), icon: <FaPlus />, path: '/admin/add-animal', color: '#2a7d40' },
      { title: t('adminAccueil.quickActions.manageAnimals', { defaultValue: 'Manage Animals' }), icon: <GiCow />, path: '/admin/animals', color: '#1a4d28' },
      { title: t('adminAccueil.quickActions.reproduction', { defaultValue: 'Reproduction' }), icon: <FaHeartbeat />, path: '/admin/reproduction', color: '#1e6131' },
      { title: t('adminAccueil.quickActions.todo', { defaultValue: 'To-Do' }), icon: <FaCheckCircle />, path: '/admin/to-do', color: '#d4a017' },
    ] : [
      { title: t('adminAccueil.quickActions.addAnimal', { defaultValue: 'Add Animal' }), icon: <FaPlus />, path: '/eleveur/add-animal', color: '#2a7d40' },
      { title: t('adminAccueil.quickActions.myAnimals', { defaultValue: 'My Animals' }), icon: <GiCow />, path: '/eleveur/animals', color: '#1a4d28' },
      { title: t('adminAccueil.quickActions.reproduction', { defaultValue: 'Reproduction' }), icon: <FaHeartbeat />, path: '/eleveur/reproduction', color: '#1e6131' },
      { title: t('adminAccueil.quickActions.todo', { defaultValue: 'To-Do' }), icon: <FaCheckCircle />, path: '/eleveur/to-do', color: '#d4a017' },
    ],
  [t, user.role]);

  const statusCards = useMemo(() => [
    {
      title: t('adminAccueil.status.gestation', { defaultValue: 'Gestation' }),
      count: stats.gestation,
      icon: <MdPregnantWoman />,
      gradient: 'linear-gradient(135deg, #1e6131 0%, #3a9e54 100%)'
    },
    {
      title: t('adminAccueil.status.calving', { defaultValue: 'Calving' }),
      count: stats.velage,
      icon: <GiBabyBottle />,
      gradient: 'linear-gradient(135deg, #d4a017 0%, #f2c84b 100%)'
    },
    {
      title: t('adminAccueil.status.mating', { defaultValue: 'Mating' }),
      count: stats.saillie,
      icon: <FaHeart />,
      gradient: 'linear-gradient(135deg, #14381f 0%, #2a7d40 100%)'
    },
    {
      title: t('adminAccueil.status.rest', { defaultValue: 'Rest' }),
      count: stats.repos,
      icon: <FaBed />,
      gradient: 'linear-gradient(135deg, #6cbf80 0%, #a8ddb5 100%)'
    },
  ], [stats, t]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const openFermeModal = () => {
    if (user?.role !== 'admin' || !user?.ferme) return;
    setFermeNom(user.ferme.nom || '');
    setFermeAdresse(user.ferme.adresse || '');
    setIsFermeModalOpen(true);
  };

  const closeFermeModal = () => {
    if (fermeLoading) return;
    setIsFermeModalOpen(false);
  };

  const handleFermeSubmit = async (e) => {
    e.preventDefault();

    if (user?.role !== 'admin') {
      return;
    }

    if (!fermeNom || fermeNom.trim() === '') {
      swal(t('adminFerme.swal.errorTitle'), t('adminFerme.swal.nameRequired'), 'error');
      return;
    }

    setFermeLoading(true);

    const fermeData = {
      nom: fermeNom,
      adresse: fermeAdresse || null,
    };

    try {
      const updatedFerme = await updateFerme(user.ferme_id, fermeData);

      setUser({
        ...user,
        ferme: updatedFerme,
      });

      swal(t('adminFerme.swal.successTitle'), t('adminFerme.swal.successText'), 'success');
      setIsFermeModalOpen(false);
    } catch (error) {
      console.error('Update error:', error);
      const errorMsg = error.response?.data?.message || t('adminFerme.swal.genericError');
      swal(t('adminFerme.swal.errorTitle'), errorMsg, 'error');
    } finally {
      setFermeLoading(false);
    }
  };

  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const dateLocale = currentLang === 'ar' ? 'ar-EG' : currentLang === 'fr' ? 'fr-FR' : 'en-US';

  return (
    <div className="accueil">
      <div className="accueil-header">
        <div className="welcome-section">
          <div className="welcome-row">
            <div className="welcome-main">
              <h1 className="welcome-title">
                <span className="user-name">{t('adminAccueil.greeting', { name: user?.nom || '', defaultValue: 'Hello {{name}}' })}</span>
              </h1>
              <p className="welcome-subtitle">
                {new Date().toLocaleDateString(dateLocale, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {user?.role === 'admin' && user?.ferme && (
              <button
                type="button"
                className="user-ferme"
                onClick={openFermeModal}
                title={t('adminAccueil.farmEdit', { defaultValue: 'Edit farm' })}
              >
                <span className="user-ferme-name">{user.ferme.nom}</span>
                <FaEdit className="user-ferme-icon" />
              </button>
            )}
          </div>
        </div>

        {stats.upcomingVelage > 0 && (
          <div className="alert-badge">
            <FaBell className="alert-icon-animate" />
            <span>{t('adminAccueil.alertBadge', {
              count: stats.upcomingVelage,
              defaultValue: '{{count}} calving(s) this week',
            })}</span>
          </div>
        )}
      </div>

      <div className="main-stats">
        <div className="stat-card-large">
          <div className="stat-icon">
            <GiCow />
          </div>
          <div className="stat-content">
            <h3>{t('adminAccueil.stats.totalAnimals', { defaultValue: 'Total Animals' })}</h3>
            <p className="stat-number">{user.role === 'admin' ? animals.length : animalsE.length}</p>
          </div>
        </div>

        <div className="stat-card-large">
          <div className="stat-icon">
            <FaHeartbeat />
          </div>
          <div className="stat-content">
            <h3>{t('adminAccueil.stats.reproductions', { defaultValue: 'Reproductions' })}</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
        </div>

        {user.role === 'admin' && (
          <div className="stat-card-large">
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-content">
              <h3>{t('adminAccueil.stats.users', { defaultValue: 'Users' })}</h3>
              <p className="stat-number">{users.length}</p>
            </div>
          </div>
        )}

        {stats.croissance >= 0 && (
          <div className="stat-card-large">
            <div className="stat-icon">
              <FaBaby />
            </div>
            <div className="stat-content">
              <h3>{t('adminAccueil.stats.growing', { defaultValue: 'In Growth' })}</h3>
              <p className="stat-number">{stats.croissance}</p>
            </div>
          </div>
        )}
      </div>

      <div className="section">
        <h2 className="section-title">
          <FaChartLine className="section-icon" />
          {t('adminAccueil.sections.distributionByStatus', { defaultValue: 'Status Distribution' })}
        </h2>
        <div className="status-grid">
          {statusCards.map((card, index) => (
            <div
              key={index}
              className="status-card"
              style={{ background: card.gradient }}
            >
              <div className="status-icon">{card.icon}</div>
              <div className="status-info">
                <h3>{card.title}</h3>
                <p className="status-count">{card.count}</p>
                <div className="status-percentage">
                  {t('adminAccueil.statusPercentage', {
                    percent: stats.total > 0 ? Math.round((card.count / stats.total) * 100) : 0,
                    defaultValue: '{{percent}}%',
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {user?.role === 'admin' && isFermeModalOpen && (
        <div className="fe-modal-overlay" onClick={closeFermeModal}>
          <div className="fe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fe-modal-header">
              <h2 className="fe-modal-title">{t('adminFerme.title')}</h2>
            </div>
            <form onSubmit={handleFermeSubmit} className="fe-modal-form">
              <div className="fe-modal-field">
                <label className="fe-modal-label">
                  <FiHome className="fe-modal-label-icon" />
                  {t('adminFerme.fields.name')}
                </label>
                <input
                  type="text"
                  className="fe-modal-input"
                  value={fermeNom}
                  onChange={(e) => setFermeNom(e.target.value)}
                  placeholder={t('adminFerme.placeholders.name')}
                  required
                />
              </div>
              <div className="fe-modal-field">
                <label className="fe-modal-label">
                  <FiMapPin className="fe-modal-label-icon" />
                  {t('adminFerme.fields.address')}
                </label>
                <input
                  type="text"
                  className="fe-modal-input"
                  value={fermeAdresse}
                  onChange={(e) => setFermeAdresse(e.target.value)}
                  placeholder={t('adminFerme.placeholders.address')}
                />
              </div>
              <div className="fe-modal-actions">
                <button
                  type="button"
                  className="fe-btn-secondary"
                  onClick={closeFermeModal}
                  disabled={fermeLoading}
                >
                  {t('adminFerme.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="fe-btn-primary"
                  disabled={fermeLoading}
                >
                  {fermeLoading
                    ? (<><FiLoader className="fe-btn-spin" /> {t('adminFerme.actions.updating')}</>)
                    : (<><FiCheckCircleOutline /> {t('adminFerme.actions.update')}</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="section">
        <h2 className="section-title">
          <FaTachometerAlt className="section-icon" />
          {t('adminAccueil.sections.quickActions', { defaultValue: 'Quick Actions' })}
        </h2>
        <div className="quick-actions">
          {quickActions.map((action, index) => (
            <div
              key={index}
              className="action-card"
              onClick={() => handleNavigate(action.path)}
              style={{ '--card-color': action.color }}
            >
              <div className="action-icon">{action.icon}</div>
              <p className="action-title">{action.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAccueil;
